import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamSearchBox } from "../shared/team-search-box";

export const CopyLatestReturnFlowCard = memo(function CopyLatestReturnFlowCard({
  sourceTeamId,
}: {
  sourceTeamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [destinationTeamId, setDestinationTeamId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const handleCopy = useHandler(async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const result = await rpcClient.copyLatestReturnFlow({
        sourceTeamId,
        destinationTeamId: destinationTeamId.trim(),
      });

      setMessage({
        type: result.success ? "success" : "error",
        text: result.message || "Operation completed",
      });

      if (result.success) {
        // Reset form on success
        setDestinationTeamId("");
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: `Failed to copy return flow: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
      });
    } finally {
      setIsLoading(false);
    }
  });

  const isFormValid = destinationTeamId.trim() !== "";

  return (
    <Card title="Copy Latest Return Flow To">
      <Flex dir="column">
        <Text fontSize="sm" textColor="secondary">
          Copy and publish the most recently published return flow from this
          team to another team
        </Text>

        <TeamSearchBox
          label="Destination Team"
          placeholder="Search by team name or enter team ID..."
          setValue={setDestinationTeamId}
          value={destinationTeamId}
        />

        <Flex>
          <RedoButton
            disabled={!isFormValid || isLoading}
            hierarchy="primary"
            onClick={handleCopy}
            text={isLoading ? "Copying..." : "Copy and Publish Return Flow"}
          />
        </Flex>

        {message && (
          <Text
            fontSize="sm"
            textColor={message.type === "success" ? "success" : "error"}
          >
            {message.text}
          </Text>
        )}
      </Flex>
    </Card>
  );
});
